/**
 * 1.7.5 — Print / Save as PDF (D-1.7.5-2).
 *
 * `<PrintPage />` is the button a page with a chart or a list carries in its head (`data-testid="print-page"`):
 * it prints the page as it is — re-flowed for Letter, on ABF letterhead, the date and who printed it in the
 * footer, no buttons and no navigation (src/styles/print175.css). `<PrintFrame />` is mounted once by the shell,
 * inside `.content` beside `<main>`, and holds the letterhead and the footer that only paper shows: in print
 * `.content` is laid out as a table whose header and footer groups they are, so the browser repeats them on every
 * page and keeps the page's content clear of them.
 */
import { useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';

/** The letterhead's words — the same the server's PDFs carry (api/src/pdf/template.ts ORG_NAME / ORG_ADDRESS; a test holds them equal). */
export const PRINT_ORG_NAME = 'Associated Blind Foundation';
export const PRINT_ORG_ADDRESS = 'Selis Manor · 135 West 23rd Street, New York, NY 10011';
/** The office's clock, whatever the device's. */
const OFFICE_ZONE = 'America/New_York';

export function PrintPage({ testId = 'print-page' }: { testId?: string }) {
  const { t } = useTranslation();
  return (
    <Button variant="secondary" size="sm" className="no-print" onClick={() => window.print()} data-testid={testId}>
      {t('print175.button')}
    </Button>
  );
}

/** Mounted once in the shell: the letterhead over the page and the footer under it, on paper only. */
export function PrintFrame({ printedBy }: { printedBy: string }) {
  const { t, i18n } = useTranslation();
  // a sensible value from the start; the real one is worked out when the print starts
  const [at, setAt] = useState(() => new Date());
  useEffect(() => {
    const onPrint = () => flushSync(() => setAt(new Date()));
    window.addEventListener('beforeprint', onPrint);
    return () => window.removeEventListener('beforeprint', onPrint);
  }, []);

  const when = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short', timeZone: OFFICE_ZONE }).format(at);
  const who = printedBy.trim();
  const app = t('app.name');
  return (
    <>
      <div className="print-letterhead" data-testid="print-letterhead">
        <div className="print-letterhead-in">
          <img src="/brand/mark-light.png" alt="" />
          <div>
            <div className="print-org">{PRINT_ORG_NAME}</div>
            <div className="print-address">{PRINT_ORG_ADDRESS}</div>
          </div>
        </div>
      </div>
      <div className="print-footer" data-testid="print-footer">
        <div className="print-footer-in">{who ? t('print175.footer', { when, who, app }) : t('print175.footerNoName', { when, app })}</div>
      </div>
    </>
  );
}
